// ---------- Types ----------
export type RGB = {
  r: number;
  g: number;
  b: number;
};

export type HSL = {
  h: number; // 0–360
  s: number; // 0–1
  l: number; // 0–1
};

export type ColorAdjustOptions = {
  sat?: number; // multiplier (e.g. 1.2 = +20% saturation)
  light?: number; // multiplier (e.g. 0.9 = -10% lightness)
};

export function hexToRgb(hex: string): RGB {
  const clean = hex.replace("#", "");

  const bigint = parseInt(clean, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

export function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);

  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;

  let s = 0;

  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      case bn:
        h = (rn - gn) / d + 4;
        break;
    }

    h *= 60;
  }

  return { h, s, l };
}

export function hslToRgb({ h, s, l }: HSL): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let rp = 0;
  let gp = 0;
  let bp = 0;

  if (h < 60) {
    rp = c;
    gp = x;
  } else if (h < 120) {
    rp = x;
    gp = c;
  } else if (h < 180) {
    gp = c;
    bp = x;
  } else if (h < 240) {
    gp = x;
    bp = c;
  } else if (h < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }

  return {
    r: Math.round((rp + m) * 255),
    g: Math.round((gp + m) * 255),
    b: Math.round((bp + m) * 255),
  };
}

export function adjustColor(rgb: RGB, { sat = 1.3, light = 1 }: ColorAdjustOptions = {}): string {
  const hsl = rgbToHsl(rgb);

  const adjusted: HSL = {
    h: hsl.h,
    s: Math.min(1, hsl.s * sat),
    l: Math.min(1, Math.max(0, hsl.l * light)),
  };

  const result = hslToRgb(adjusted);

  return `rgb(${result.r}, ${result.g}, ${result.b})`;
}
